import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Shield, Search, Crown, X, Check, MoreVertical, Copy, Eraser, ArrowLeftRight, Loader2 } from 'lucide-react';

// ============================================================
// PermissionMatrix.jsx  —  the audit + bulk companion to the overlay.
//
// NOT an everyday editor (use the in-app Annotate overlay for "who gets
// this button"). This screen is for the things the overlay can't do:
//   • see EVERY role x capability at once (audit / reverse-lookup)
//   • set up a new role fast (copy another role's grants, clear all)
//   • grant permissions that have no on-screen <Gate> element
//
// Wired to the same API your PermissionManager uses:
//   GET  /roles                 -> [{ RoleID, RoleName }]
//   GET  /permissions/modules   -> [{ ModuleID, ModuleCode, ModuleName }]
//   GET  /permissions/features  -> flattened rows [{ FeatureID, ModuleID,
//                                  FeatureCode, FeatureName, SortOrder,
//                                  ActionID, ActionCode, ActionName }]
//   GET  /permissions/roles/:id -> [{ FeatureID, ActionID }]
//   PUT  /permissions/roles/:id -> body { permissions:[{featureId,actionId}] }
//
// Drop into components/ and render it for the 'permissions' route.
// ============================================================

const SUPERUSER = 'GlobalAdmin';

const TONE = {
  view: 'bg-sky-100 text-sky-700 border-sky-200',
  create: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  edit: 'bg-amber-100 text-amber-700 border-amber-200',
  delete: 'bg-rose-100 text-rose-700 border-rose-200',
  print: 'bg-violet-100 text-violet-700 border-violet-200',
  process: 'bg-teal-100 text-teal-700 border-teal-200',
};
const tone = (code) => TONE[code] || 'bg-slate-100 text-slate-600 border-slate-200';

const PermissionMatrix = ({ apiCall, showSuccess, showError }) => {
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [features, setFeatures] = useState([]); // flattened rows
  const [grants, setGrants] = useState({});     // RoleID -> Set("FeatureID_ActionID")
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState(null);
  const [search, setSearch] = useState('');
  const [openCell, setOpenCell] = useState(null);  // `${RoleID}:${FeatureID}`
  const [openMenu, setOpenMenu] = useState(null);   // RoleID

  // ---- load everything ----
  useEffect(() => {
    (async () => {
      try {
        const [rolesData, modulesData, featuresData] = await Promise.all([
          apiCall('/roles'),
          apiCall('/permissions/modules'),
          apiCall('/permissions/features'),
        ]);
        setRoles(rolesData);
        setModules(modulesData);
        setFeatures(featuresData);

        const grantMap = {};
        await Promise.all(rolesData.map(async (r) => {
          try {
            const perms = await apiCall(`/permissions/roles/${r.RoleID}`);
            grantMap[r.RoleID] = new Set(perms.map((p) => `${p.FeatureID}_${p.ActionID}`));
          } catch {
            grantMap[r.RoleID] = new Set();
          }
        }));
        setGrants(grantMap);
      } catch (e) {
        showError && showError('Failed to load permissions: ' + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [apiCall, showError]);

  // ---- group flattened feature rows into module -> features[{actions[]}] ----
  const grouped = useMemo(() => {
    const featureMap = {};
    features.forEach((f) => {
      if (!featureMap[f.FeatureID]) featureMap[f.FeatureID] = { ...f, actions: [] };
      if (f.ActionID != null && !featureMap[f.FeatureID].actions.some(a => a.ActionID === f.ActionID)) {
        featureMap[f.FeatureID].actions.push({ ActionID: f.ActionID, ActionCode: f.ActionCode, ActionName: f.ActionName });
      }
    });
    const byModule = modules.map((m) => ({
      module: m,
      features: Object.values(featureMap)
        .filter((f) => f.ModuleID === m.ModuleID)
        .sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0)),
    }));
    if (!search.trim()) return byModule;
    const q = search.toLowerCase();
    return byModule
      .map((g) => ({
        ...g,
        features: g.features.filter(
          (f) => (f.FeatureName || '').toLowerCase().includes(q) || (g.module.ModuleName || '').toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.features.length);
  }, [features, modules, search]);

  const isSuper = (role) => role.RoleName === SUPERUSER;

  const persist = useCallback(async (roleId, set) => {
    setSavingRole(roleId);
    try {
      const permissions = [...set].map((k) => {
        const [featureId, actionId] = k.split('_').map(Number);
        return { featureId, actionId };
      });
      await apiCall(`/permissions/roles/${roleId}`, { method: 'PUT', body: JSON.stringify({ permissions }) });
    } catch (e) {
      showError && showError('Save failed: ' + e.message);
    } finally {
      setSavingRole(null);
    }
  }, [apiCall, showError]);

  const toggleAction = (roleId, featureId, actionId) => {
    const key = `${featureId}_${actionId}`;
    const next = new Set(grants[roleId] || []);
    next.has(key) ? next.delete(key) : next.add(key);
    setGrants((g) => ({ ...g, [roleId]: next }));
    persist(roleId, next);
  };

  const copyFrom = (sourceId, targetId) => {
    const next = new Set(grants[sourceId] || []);
    setGrants((g) => ({ ...g, [targetId]: next }));
    persist(targetId, next);
    setOpenMenu(null);
    const src = roles.find(r => r.RoleID === sourceId)?.RoleName;
    const tgt = roles.find(r => r.RoleID === targetId)?.RoleName;
    showSuccess && showSuccess(`Copied ${src}'s access to ${tgt}`);
  };

  const clearRole = (roleId) => {
    const next = new Set();
    setGrants((g) => ({ ...g, [roleId]: next }));
    persist(roleId, next);
    setOpenMenu(null);
  };

  const grantedActions = (roleId, feature) => {
    const set = grants[roleId];
    if (!set) return [];
    return feature.actions.filter((a) => set.has(`${feature.FeatureID}_${a.ActionID}`));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-slate-400">
        <Loader2 className="animate-spin mr-2" size={20} /> Loading access matrix…
      </div>
    );
  }

  return (
    <div className="max-w-full" onClick={() => { setOpenCell(null); setOpenMenu(null); }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-600 text-white p-2.5 rounded-xl"><Shield size={20} /></div>
          <div>
            <h1 className="text-xl font-bold text-slate-800">Access Matrix</h1>
            <p className="text-sm text-slate-500">Every role × capability. Audit, copy a role, or grant element-less permissions.</p>
          </div>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Find a module or feature"
            className="pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm w-64 focus:ring-2 focus:ring-indigo-400 outline-none"
          />
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs text-slate-500">
        <span className="font-medium">Actions:</span>
        {['view', 'create', 'edit', 'print', 'delete', 'process'].map((a) => (
          <span key={a} className={`px-2 py-0.5 rounded border capitalize ${tone(a)}`}>{a}</span>
        ))}
        <span className="ml-1 inline-flex items-center gap-1 text-slate-400"><ArrowLeftRight size={12} /> click a cell to edit · use a role's ⋮ menu to copy or clear</span>
      </div>

      {/* Matrix */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left font-semibold text-slate-500 px-4 py-3 sticky left-0 bg-white z-10 min-w-[220px]">Capability</th>
              {roles.map((r) => (
                <th key={r.RoleID} className="px-2 py-3 min-w-[110px] align-bottom">
                  <div className="flex flex-col items-center gap-1 relative">
                    <span className={`text-xs font-semibold text-center leading-tight ${isSuper(r) ? 'text-indigo-600' : 'text-slate-700'}`}>
                      {isSuper(r) && <Crown size={11} className="inline mr-0.5 -mt-0.5" />}
                      {r.RoleName}
                    </span>
                    {!isSuper(r) && (
                      <button
                        onClick={(e) => { e.stopPropagation(); setOpenMenu(openMenu === r.RoleID ? null : r.RoleID); }}
                        className="text-slate-300 hover:text-slate-600"
                        title="Copy from / clear"
                      >
                        {savingRole === r.RoleID ? <Loader2 size={13} className="animate-spin" /> : <MoreVertical size={13} />}
                      </button>
                    )}
                    {openMenu === r.RoleID && (
                      <div className="absolute top-full mt-1 z-30 w-48 bg-white rounded-lg border border-slate-200 shadow-xl p-1 text-left" onClick={(e) => e.stopPropagation()}>
                        <p className="text-[10px] uppercase tracking-wide text-slate-400 px-2 py-1">Copy grants from</p>
                        {roles.filter((s) => s.RoleID !== r.RoleID && !isSuper(s)).map((s) => (
                          <button key={s.RoleID} onClick={() => copyFrom(s.RoleID, r.RoleID)}
                            className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-indigo-50 text-slate-600 flex items-center gap-2">
                            <Copy size={12} /> {s.RoleName}
                          </button>
                        ))}
                        <div className="border-t border-slate-100 my-1" />
                        <button onClick={() => clearRole(r.RoleID)}
                          className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-rose-50 text-rose-600 flex items-center gap-2">
                          <Eraser size={12} /> Clear all
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((g) => (
              <React.Fragment key={g.module.ModuleID}>
                <tr className="bg-slate-50/80">
                  <td colSpan={roles.length + 1} className="px-4 py-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-400 sticky left-0 bg-slate-50/80">
                    {g.module.ModuleName}
                  </td>
                </tr>
                {g.features.map((feature) => (
                  <tr key={feature.FeatureID} className="border-b border-slate-100 hover:bg-slate-50/40">
                    <td className="px-4 py-2.5 sticky left-0 bg-white z-10 font-medium text-slate-700">{feature.FeatureName}</td>
                    {roles.map((r) => {
                      const cellKey = `${r.RoleID}:${feature.FeatureID}`;
                      const acts = grantedActions(r.RoleID, feature);
                      return (
                        <td key={r.RoleID} className="px-1.5 py-2 text-center relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); if (!isSuper(r)) setOpenCell(openCell === cellKey ? null : cellKey); }}
                            className={`w-full min-h-[32px] rounded-lg border flex flex-wrap items-center justify-center gap-0.5 px-1 py-1 transition
                              ${isSuper(r) ? 'border-transparent cursor-default'
                                : acts.length ? 'border-transparent hover:bg-slate-50' : 'border-dashed border-slate-200 hover:border-slate-300'}`}
                          >
                            {isSuper(r) ? (
                              <span className="text-[10px] font-semibold text-indigo-600">ALL</span>
                            ) : acts.length ? (
                              acts.map((a) => (
                                <span key={a.ActionID} className={`text-[9px] px-1 py-0.5 rounded border font-medium ${tone(a.ActionCode)}`}>
                                  {(a.ActionCode || '?')[0].toUpperCase()}
                                </span>
                              ))
                            ) : (
                              <span className="text-slate-300 text-xs">·</span>
                            )}
                          </button>

                          {openCell === cellKey && !isSuper(r) && (
                            <div className="absolute z-30 top-full mt-1 left-1/2 -translate-x-1/2 w-52 bg-white rounded-xl border border-slate-200 shadow-2xl p-3 text-left" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-xs font-bold text-slate-700">{feature.FeatureName}</p>
                                  <p className="text-[10px] text-slate-400">{r.RoleName}</p>
                                </div>
                                <button onClick={() => setOpenCell(null)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                              </div>
                              <div className="space-y-1">
                                {feature.actions.map((a) => {
                                  const on = (grants[r.RoleID] || new Set()).has(`${feature.FeatureID}_${a.ActionID}`);
                                  return (
                                    <button key={a.ActionID} onClick={() => toggleAction(r.RoleID, feature.FeatureID, a.ActionID)}
                                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded-lg text-xs border transition
                                        ${on ? `${tone(a.ActionCode)} font-medium` : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                      <span className="capitalize">{a.ActionName || a.ActionCode}</span>
                                      {on ? <Check size={13} /> : <span className="w-3 h-3 rounded border border-slate-300" />}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-slate-400 mt-3">
        Each letter is a granted action (V/C/E/P/D). “ALL” = Global Admin (bypasses checks). Changes save instantly per role.
        Users must re-login to pick up new grants.
      </p>
    </div>
  );
};

export default PermissionMatrix;
